// ============================================
// ResetPassword — Página /reset-password?token=...&email=...
// Captura la nueva contraseña y la confirma contra Medusa v2:
//   POST /auth/customer/emailpass/update  (Authorization: Bearer <reset_token>)
// El token viene del correo enviado por el subscriber password-reset.
// ============================================

import React, { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { MEDUSA_API_URL, MEDUSA_API_KEY } from '../services/medusa/config';
import SEO from '../components/SEO';
import './ForgotPassword.css';

const MIN_LEN = 8;

export default function ResetPassword() {
  const location = useLocation();
  const navigate = useNavigate();

  const { token, email } = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return {
      token: sp.get('token') || '',
      email: sp.get('email') || '',
    };
  }, [location.search]);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const tokenMissing = !token;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < MIN_LEN) {
      setError(`La contraseña debe tener al menos ${MIN_LEN} caracteres.`);
      return;
    }
    if (password !== confirm) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${MEDUSA_API_URL}/auth/customer/emailpass/update`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-publishable-api-key': MEDUSA_API_KEY,
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ password }),
        }
      );

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        const msg =
          data?.message ||
          'No pudimos actualizar la contraseña. El enlace puede haber caducado.';
        throw new Error(msg);
      }

      setDone(true);
      setTimeout(() => navigate('/account'), 2500);
    } catch (err) {
      setError(
        err.message ||
          'No pudimos actualizar la contraseña. Solicita un nuevo enlace.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="forgot-page">
      <SEO
        title="Restablecer contraseña"
        description="Define una nueva contraseña para tu cuenta Physiology"
        path="/reset-password"
        noindex
      />
      <div className="forgot-card">
        <header className="forgot-header">
          <span className="forgot-eyebrow">Cuenta Physiology</span>
          <h1 className="forgot-title">Restablecer contraseña</h1>
        </header>

        {tokenMissing ? (
          <>
            <p className="forgot-lead">
              Este enlace no es válido. Solicita uno nuevo desde la página de
              recuperación.
            </p>
            <div className="forgot-footer">
              <Link to="/forgot-password" className="forgot-back">
                ← Solicitar nuevo enlace
              </Link>
            </div>
          </>
        ) : done ? (
          <div className="forgot-success">
            <p className="forgot-success-headline">
              Contraseña actualizada.
            </p>
            <p className="forgot-success-body">
              Ya puedes iniciar sesión con tu nueva contraseña. Te llevamos al
              panel de tu cuenta.
            </p>
          </div>
        ) : (
          <>
            <p className="forgot-lead">
              Define una nueva contraseña para
              {email ? <> <strong>{email}</strong>.</> : ' tu cuenta.'} Mínimo{' '}
              {MIN_LEN} caracteres.
            </p>

            <form className="forgot-form" onSubmit={handleSubmit}>
              <label htmlFor="reset-password" className="forgot-label">
                Nueva contraseña
              </label>
              <input
                id="reset-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
                placeholder="••••••••"
                className="forgot-input"
                autoComplete="new-password"
                minLength={MIN_LEN}
              />

              <label
                htmlFor="reset-confirm"
                className="forgot-label"
                style={{ marginTop: '1rem' }}
              >
                Confirmar contraseña
              </label>
              <input
                id="reset-confirm"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                disabled={loading}
                placeholder="••••••••"
                className="forgot-input"
                autoComplete="new-password"
                minLength={MIN_LEN}
              />

              {error && <p className="forgot-error">{error}</p>}

              <button
                type="submit"
                className="forgot-submit"
                disabled={loading || !password || !confirm}
              >
                {loading ? 'Guardando…' : 'Guardar nueva contraseña'}
              </button>
            </form>
          </>
        )}

        <div className="forgot-footer">
          <Link to="/" className="forgot-back">
            ← Volver a la tienda
          </Link>
        </div>
      </div>
    </div>
  );
}
